import * as React from "react"
import {
  useChartBase,
  injectStyle,
  useMeasuredWidth,
  seriesColor,
  resolveColor,
  type NumberFormatter,
} from "./chartStyles"

/*
 * MARK V — LineChart. Paths SVG medidos à largura real (ResizeObserver) p/
 * strokes nítidos. Grid + labels de categoria em HTML (tipo nunca escala).
 * Suporta múltiplas séries, fill de área na primeira, dots e pontos null (gaps).
 * Modo `interactive`: scrubber por teclado/ponteiro + guia vertical + tooltip
 * com os números do ponto. Porte fiel de
 * mark-v-design-system/components/charts/LineChart.jsx.
 */

const CSS = `
.mv-line{ position:relative; }
.mv-line__plot{ position:relative; width:100%; }
.mv-line__grid{ position:absolute; inset:0; pointer-events:none; }
.mv-line__gl{ position:absolute; left:0; right:0; border-top:1px solid var(--markv-chart-grid); }
.mv-line__yt{ position:absolute; left:0; transform:translateY(-50%); }
.mv-line__svg{ position:absolute; inset:0; overflow:visible; }
.mv-line__cats{ display:flex; justify-content:space-between; margin-top:8px; }
.mv-line__cat{ flex:1; text-align:center; }
.mv-line__cat:first-child{ text-align:left; }
.mv-line__cat:last-child{ text-align:right; }
.mv-line__hit{ position:absolute; inset:0; cursor:crosshair; touch-action:none;
  border-radius:var(--markv-radius); }
.mv-line__hit:focus-visible{ outline:none; box-shadow:var(--markv-shadow-focus); }
.mv-line__tip{ position:absolute; top:4px; z-index:2; pointer-events:none;
  min-width:max-content; padding:8px 10px; border-radius:var(--markv-radius);
  background:var(--markv-surface-50); border:1px solid var(--markv-chart-grid);
  box-shadow:var(--markv-shadow-md); }
.mv-line__tip-cat{ font-size:var(--markv-text-2xs); text-transform:uppercase;
  letter-spacing:.04em; color:var(--markv-chart-label); margin-bottom:6px; }
.mv-line__tip-row{ display:flex; align-items:center; gap:8px; line-height:1.5; }
.mv-line__tip-row + .mv-line__tip-row{ margin-top:2px; }
.mv-line__tip-name{ font-size:var(--markv-text-xs); color:var(--markv-text-500);
  margin-right:auto; }
.mv-line__tip-val{ font-size:var(--markv-text-xs); font-variant-numeric:tabular-nums;
  font-weight:var(--markv-weight-semibold); color:var(--markv-chart-value); }
.mv-chart__sw--dash{ background:none !important; border-top:2px dashed var(--markv-chart-label);
  height:0; border-radius:0; }
`

export type LinePoint = number | null

export type LineSeries = {
  name: string
  points: ReadonlyArray<LinePoint>
  color?: string
  dashed?: boolean
}

export type LineChartProps = {
  series: LineSeries[]
  labels: string[]
  height?: number
  min?: number
  max?: number
  area?: boolean
  dots?: boolean
  yTicks?: number
  format?: NumberFormatter
  /** Habilita scrubber (teclado/ponteiro) + tooltip com os números do ponto. */
  interactive?: boolean
  /** Rótulo acessível do scrubber (obrigatório quando `interactive`). */
  ariaLabel?: string
  className?: string
}

export function LineChart({
  series,
  labels,
  height = 200,
  min,
  max,
  area = false,
  dots = true,
  yTicks = 4,
  format = (v) => v,
  interactive = false,
  ariaLabel,
  className = "",
}: LineChartProps) {
  useChartBase()
  React.useEffect(() => injectStyle("mv-line-styles", CSS), [])
  const ref = React.useRef<HTMLDivElement>(null)
  const W = useMeasuredWidth(ref)
  const gutter = yTicks > 0 ? 40 : 0
  const padX = 6
  const innerW = Math.max(0, W - gutter - padX)
  const H = height

  const all = series.flatMap((s) => s.points).filter((v): v is number => v !== null)
  const lo = min ?? Math.min(...all, 0)
  const hi = max ?? Math.max(...all, 1)
  const span = hi - lo || 1
  const n = labels.length

  const x = (i: number) => gutter + (n > 1 ? (i / (n - 1)) * innerW : innerW / 2)
  const y = (v: number) => H - ((v - lo) / span) * H

  const ticks: number[] = []
  for (let i = 0; i <= yTicks; i++) ticks.push(lo + (span / yTicks) * i)

  // Path só sobre segmentos contíguos não-nulos (levanta a caneta nos gaps).
  const linePath = (pts: ReadonlyArray<LinePoint>) => {
    let d = ""
    let pen = false
    pts.forEach((v, i) => {
      if (v === null) {
        pen = false
        return
      }
      d += `${pen ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)} `
      pen = true
    })
    return d.trim()
  }
  const areaPath = (pts: ReadonlyArray<LinePoint>) => {
    const idx = pts.map((v, i) => (v === null ? -1 : i)).filter((i) => i >= 0)
    if (idx.length === 0) return ""
    const first = idx[0]!
    const last = idx[idx.length - 1]!
    return `${linePath(pts)} L${x(last).toFixed(1)},${H} L${x(first).toFixed(1)},${H} Z`
  }

  const first = series[0]

  // ── Scrubber interativo ──────────────────────────────────────────────────
  const [active, setActive] = React.useState<number | null>(null)
  const clampIdx = (i: number) => Math.max(0, Math.min(n - 1, i))

  const idxFromPointer = (clientX: number): number => {
    const node = ref.current
    if (!node || innerW <= 0 || n <= 1) return clampIdx(0)
    const rect = node.getBoundingClientRect()
    const rel = clientX - rect.left - gutter
    return clampIdx(Math.round((rel / innerW) * (n - 1)))
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (n === 0) return
    const cur = active ?? n - 1
    if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault()
      setActive(clampIdx(cur - 1))
    } else if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault()
      setActive(clampIdx(cur + 1))
    } else if (e.key === "Home") {
      e.preventDefault()
      setActive(0)
    } else if (e.key === "End") {
      e.preventDefault()
      setActive(n - 1)
    } else if (e.key === "Escape") {
      setActive(null)
    }
  }

  // Leitura textual do ponto ativo (SR via aria-valuetext do slider).
  const readout =
    active === null
      ? undefined
      : `${labels[active] ?? ""}: ` +
        series
          .map((s) => {
            const v = s.points[active]
            return v === null || v === undefined ? null : `${s.name} ${format(v)}`
          })
          .filter((t): t is string => t !== null)
          .join(", ")

  // Ancoragem horizontal do tooltip: acompanha o ponto e vira a âncora nas bordas.
  const tipStyle = (i: number): React.CSSProperties => {
    const ratio = n > 1 ? i / (n - 1) : 0.5
    const transform =
      ratio < 0.25 ? "translateX(0)" : ratio > 0.75 ? "translateX(-100%)" : "translateX(-50%)"
    return { left: x(i), transform }
  }

  return (
    <div className={`mv-chart mv-line ${className}`.trim()}>
      <div className="mv-line__plot" ref={ref} style={{ height: H }}>
        <div className="mv-line__grid">
          {ticks.map((t, i) => (
            <div
              key={i}
              className="mv-line__gl"
              style={{ bottom: `${((t - lo) / span) * 100}%`, left: gutter }}
            />
          ))}
          {yTicks > 0 &&
            ticks.map((t, i) => (
              <span
                key={`t${i}`}
                className="mv-chart__tick mv-line__yt"
                style={{ bottom: `${((t - lo) / span) * 100}%` }}
              >
                {format(Math.round(t))}
              </span>
            ))}
        </div>
        {W > 0 && (
          <svg
            className="mv-line__svg"
            width={W}
            height={H}
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
          >
            {area && first && (
              <path d={areaPath(first.points)} fill="var(--markv-chart-area)" stroke="none" />
            )}
            {interactive && active !== null && (
              <line
                x1={x(active)}
                x2={x(active)}
                y1={0}
                y2={H}
                stroke="var(--markv-chart-axis)"
                strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            )}
            {series.map((s, si) => {
              const color = resolveColor(s.color, seriesColor(si))
              return (
                <g key={si}>
                  <path
                    d={linePath(s.points)}
                    fill="none"
                    stroke={color}
                    strokeWidth="2.5"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                    {...(s.dashed ? { strokeDasharray: "5 4" } : {})}
                    vectorEffect="non-scaling-stroke"
                  />
                  {dots &&
                    s.points.map((v, i) =>
                      v === null ? null : (
                        <circle
                          key={i}
                          cx={x(i)}
                          cy={y(v)}
                          r="3.5"
                          fill="var(--markv-surface-50)"
                          stroke={color}
                          strokeWidth="2"
                        />
                      ),
                    )}
                  {interactive &&
                    active !== null &&
                    s.points[active] !== null &&
                    s.points[active] !== undefined && (
                      <circle
                        cx={x(active)}
                        cy={y(s.points[active] as number)}
                        r="4.5"
                        fill="var(--markv-surface-50)"
                        stroke={color}
                        strokeWidth="2.5"
                      />
                    )}
                </g>
              )
            })}
          </svg>
        )}
        {interactive && (
          <div
            className="mv-line__hit"
            role="slider"
            tabIndex={0}
            aria-label={ariaLabel ?? "Gráfico de linha"}
            aria-valuemin={0}
            aria-valuemax={Math.max(0, n - 1)}
            aria-valuenow={active ?? 0}
            {...(readout ? { "aria-valuetext": readout } : {})}
            onPointerMove={(e) => setActive(idxFromPointer(e.clientX))}
            onPointerLeave={() => setActive(null)}
            onFocus={() => setActive((a) => (a === null ? n - 1 : a))}
            onBlur={() => setActive(null)}
            onKeyDown={onKeyDown}
          />
        )}
        {interactive && active !== null && (
          <div className="mv-line__tip" style={tipStyle(active)} data-testid="mv-line-tip" aria-hidden="true">
            <div className="mv-line__tip-cat">{labels[active]}</div>
            {series.map((s, si) => {
              const v = s.points[active]
              if (v === null || v === undefined) return null
              return (
                <div className="mv-line__tip-row" key={si}>
                  <span
                    className={`mv-chart__sw ${s.dashed ? "mv-chart__sw--dash" : "mv-chart__sw--line"}`}
                    style={s.dashed ? undefined : { background: resolveColor(s.color, seriesColor(si)) }}
                  />
                  <span className="mv-line__tip-name">{s.name}</span>
                  <span className="mv-line__tip-val">{format(v)}</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
      <div className="mv-line__cats" style={{ paddingLeft: gutter }}>
        {labels.map((l, i) => (
          <span key={i} className="mv-line__cat mv-chart__cat">
            {l}
          </span>
        ))}
      </div>
      {series.length > 1 && (
        <div className="mv-chart__legend">
          {series.map((s, si) => (
            <span key={si} className="mv-chart__lg">
              <span
                className={`mv-chart__sw ${s.dashed ? "mv-chart__sw--dash" : "mv-chart__sw--line"}`}
                style={s.dashed ? undefined : { background: resolveColor(s.color, seriesColor(si)) }}
              />
              {s.name}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
